import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://23controls.com/api/v1';
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token } = response.data;
          localStorage.setItem('access_token', access_token);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  signup: (data) => apiClient.post('/auth/signup', data),
  login: (data) => apiClient.post('/auth/login', data),
  logout: (refreshToken) => apiClient.post('/auth/logout', { refresh_token: refreshToken }),
  refreshToken: (refreshToken) => apiClient.post('/auth/refresh', { refresh_token: refreshToken }),
  getCurrentUser: () => apiClient.get('/auth/me'),
  setupMFA: () => apiClient.post('/auth/mfa/setup'),
  verifyMFA: (token) => apiClient.post('/auth/mfa/verify', { token }),
  
  // OAuth
  initiateOAuth: (provider) => {
    window.location.href = `${API_URL}/auth/oauth/${provider}`;
  },
};

// User API
export const userApi = {
  getProfile: () => apiClient.get('/users/me'),
  updateProfile: (data) => apiClient.put('/users/me', data),
  changePassword: (data) => apiClient.post('/users/me/change-password', data),
  
  // Admin endpoints
  listUsers: (skip = 0, limit = 100) => apiClient.get(`/users?skip=${skip}&limit=${limit}`),
  getUser: (userId) => apiClient.get(`/users/${userId}`),
  assignRole: (userId, roleId) => apiClient.post(`/users/${userId}/roles/${roleId}`),
  removeRole: (userId, roleId) => apiClient.delete(`/users/${userId}/roles/${roleId}`),
  
  // Roles
  createRole: (data) => apiClient.post('/users/roles', data),
  listRoles: () => apiClient.get('/users/roles'),
  
  // Permissions
  createPermission: (data) => apiClient.post('/users/permissions', data),
  listPermissions: () => apiClient.get('/users/permissions'),
};

export default apiClient;